import { useEffect, useState } from "react";
import { Link2, Banknote, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function RentCollection({ propertyId = "default" }) {
  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ tenant_name: "", rent_amount: "", frequency: "monthly", start_date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: ags }, { data: fin }] = await Promise.all([
        api.get("/rent-agreements"),
        api.get(`/property-financials/${propertyId}`),
      ]);
      setAgreements(ags || []);
      setTotal(fin?.actuals?.total_rent_received ?? 0);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.tenant_name.trim() || !Number(form.rent_amount)) { toast.error("Tenant name + rent amount required"); return; }
    setBusy(true);
    try {
      await api.post(`/property-financials/${propertyId}/rent-agreement`, { ...form, rent_amount: Number(form.rent_amount) });
      toast.success("Rent agreement created");
      setForm({ ...form, tenant_name: "", rent_amount: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not create agreement");
    } finally { setBusy(false); }
  };

  const getLink = async (id) => {
    try {
      const { data } = await api.post(`/rent-agreements/${id}/collect-link`, { origin_url: window.location.origin });
      await navigator.clipboard.writeText(data.url);
      toast.success("Stripe payment link copied — send it to your tenant 💷");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create payment link");
    }
  };

  const logPayment = async (ag) => {
    try {
      await api.post(`/rent-agreements/${ag.agreement_id}/log-payment`, { amount: ag.rent_amount });
      toast.success("Rent payment logged");
      load();
    } catch { toast.error("Could not log payment"); }
  };

  const cancel = async (id) => {
    try { await api.post(`/rent-agreements/${id}/cancel`); toast.success("Agreement cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <div className="mt-4" data-testid="rent-collection">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-center">
        <div className="font-display text-2xl text-emerald-500" data-testid="rent-total-received">£{Number(total).toLocaleString()}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total rent received (verified)</div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-xs text-muted-foreground">Tenant name
          <input value={form.tenant_name} data-testid="rent-input-tenant" onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
        <label className="text-xs text-muted-foreground">Rent amount (£)
          <input type="number" value={form.rent_amount} data-testid="rent-input-amount" onChange={(e) => setForm({ ...form, rent_amount: e.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
        <label className="text-xs text-muted-foreground">Frequency
          <select value={form.frequency} data-testid="rent-input-frequency" onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>
        <label className="text-xs text-muted-foreground">Start date
          <input type="date" value={form.start_date} data-testid="rent-input-start" onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground" />
        </label>
      </div>
      <Button onClick={create} disabled={busy} size="sm" className="mt-3" data-testid="rent-create-agreement-btn">
        {busy ? "Creating…" : "Create rent agreement"}
      </Button>

      {agreements.length > 0 && (
        <div className="mt-5 space-y-3">
          {agreements.map((ag) => (
            <div key={ag.agreement_id} className={`rounded-xl border p-4 ${ag.status === "active" ? "border-border bg-background" : "border-border/50 bg-background/50 opacity-60"}`} data-testid={`rent-agreement-${ag.agreement_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{ag.tenant_name}</div>
                  <div className="text-xs text-muted-foreground">£{Number(ag.rent_amount).toLocaleString()} / {ag.frequency} · from {ag.start_date} · <span className={ag.status === "active" ? "text-emerald-500" : "text-red-400"}>{ag.status}</span></div>
                </div>
                {ag.status === "active" && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => getLink(ag.agreement_id)} data-testid={`rent-stripe-link-${ag.agreement_id}`}>
                      <Link2 className="mr-1 h-3 w-3" /> Stripe payment link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => logPayment(ag)} data-testid={`rent-log-payment-${ag.agreement_id}`}>
                      <Banknote className="mr-1 h-3 w-3" /> Log payment
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => cancel(ag.agreement_id)} data-testid={`rent-cancel-${ag.agreement_id}`}>
                      <XCircle className="mr-1 h-3 w-3" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">Auto-collect: generate a Stripe link and send it to your tenant — payments map straight into your Property Profit Dashboard.</p>
    </div>
  );
}
